import React, { useState } from 'react';
import { Card, Button, Table, Badge, Spinner, Modal, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { DUYET_TIEULUANTONGQUAN_API, TIEULUANTONGQUAN_API } from '@app/utils/apiUrl';
import { trangThaiTieuLuanTongQuanValue, objectTrangThaiTieuLuanTongQuan } from '@app/utils/define';
import { Get, Put, DeleteSingle } from '@app/utils/httpProvider';
import moment from 'moment';
import { toast } from 'react-toastify';
import TieuLuanTongQuanEdit from './TieuLuanTongQuanEdit';
import TieuLuanTongQuanAdd from './TieuLuanTongQuanAdd';
import ChamTieuLuanModal from './ChamTieuLuanModal';
import { ConfirmModal } from '@app/modules/modal/ConfirmModal';

const DuyetTieuLuanTongQuan = ({ hoSoId }) => {
    const [showModal, setShowModal] = useState(false);
    const [viewMode, setViewMode] = useState('list');
    const [dataEdit, setDataEdit] = useState(null);
    const [trangThai, setTrangThai] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    
    // Delete state
    const [openConfirmDeleteModal, setOpenConfirmDeleteModal] = useState(false);
    const [idWantDelete, setIdWantDelete] = useState('');
    const [confirmDelMessage, setConfirmDelMessage] = useState('');
    
    const queryClient = useQueryClient();

    const { isLoading, data: queryData, refetch } = useQuery(
        ['duyetTieuLuanTongQuanData', hoSoId],
        () => Get(DUYET_TIEULUANTONGQUAN_API.GET_PAGING, null, { HoSoNghienCuuSinhId: hoSoId, PageIndex: 1, PageSize: 100 }),
        { 
            enabled: !!hoSoId,
            keepPreviousData: true
        }
    );

    let dataList = [];
    if (queryData) {
        if (Array.isArray(queryData)) {
            dataList = queryData;
        } else if (queryData.data && Array.isArray(queryData.data)) {
            dataList = queryData.data;
        } else if (queryData.data?.$values) {
            dataList = queryData.data.$values;
        }
    }

    const handleAction = (item) => {
        setDataEdit(item);
        setTrangThai(item.trangThai || trangThaiTieuLuanTongQuanValue.chuaThucHien);
        setShowModal(true);
    };

    const deleteFunc = useMutation((id) => DeleteSingle(TIEULUANTONGQUAN_API.DELETE_ITEM, id, null));

    const deleteAction = (id, name) => {
        setIdWantDelete(id);
        setConfirmDelMessage(name || 'Bản ghi này');
        setOpenConfirmDeleteModal(true);
    };

    const acceptDelete = () => {
        if (!idWantDelete) return;

        deleteFunc.mutate(idWantDelete, {
            onSuccess: (res) => {
                if (res && (res.status === 200 || res.status === 201 || res.status === 204)) {
                    toast.success('Xóa thành công');
                    queryClient.invalidateQueries(['duyetTieuLuanTongQuanData', hoSoId]);
                } else {
                    toast.error(res?.message || 'Có lỗi xảy ra khi xóa');
                }
            },
            onError: (err) => {
                toast.error(err?.message || 'Có lỗi xảy ra');
            }
        });

        setOpenConfirmDeleteModal(false);
        setIdWantDelete('');
    };

    const cancelDelete = () => {
        setIdWantDelete('');
        setOpenConfirmDeleteModal(false);
    };

    const renderTooltip = (props, text) => (
        <Tooltip id="button-tooltip" {...props}>
            {text}
        </Tooltip>
    );

    const submitDuyet = async () => {
        if (!trangThai) {
            toast.warning('Vui lòng chọn trạng thái!');
            return;
        }

        const payload = {
            HoSoNghienCuuSinhId: hoSoId,
            TenTieuLuan: dataEdit.tenTieuLuan || '',
            TuNgay: dataEdit.tuNgay || null,
            DenNgay: dataEdit.denNgay || null,
            TrangThai: trangThai
        };

        setIsSaving(true);
        try {
            const res = await Put(DUYET_TIEULUANTONGQUAN_API.DUYET_HO_SO, dataEdit.id, payload);
            if (res && (res.status === 200 || res.status === 201 || res.status === 204 || res.id || res.success)) {
                toast.success('Lưu thành công!');
                refetch();
                setShowModal(false);
            } else {
                toast.error(res?.message || 'Có lỗi xảy ra');
            }
        } catch (error) {
            toast.error(error?.message || 'Có lỗi xảy ra');
        } finally {
            setIsSaving(false);
        }
    };

    const renderTrangThai = (val) => {
        switch (val) {
            case trangThaiTieuLuanTongQuanValue.chuaThucHien: return <Badge variant="warning">Chưa thực hiện</Badge>;
            case trangThaiTieuLuanTongQuanValue.dangThucHien: return <Badge variant="info">Đang thực hiện</Badge>;
            case trangThaiTieuLuanTongQuanValue.hoanThanh: return <Badge variant="success">Hoàn thành</Badge>;
            default: return <Badge variant="secondary">Chưa xác định</Badge>;
        }
    };

    return (
        <>
        <Card className="h-100">
            <Card.Header>
                <div className="d-flex justify-content-between align-items-center">
                    <h5 className="font-weight-bold text-primary m-0">Tiểu luận tổng quan</h5>
                    <Button variant="success" size="sm" onClick={() => setViewMode('add')}>
                        <i className="fas fa-plus mr-1"></i> HLĐT Thêm mới
                    </Button>
                </div>
            </Card.Header>
            <Card.Body>
                {isLoading ? (
                    <div className="text-center p-5">
                        <Spinner animation="border" variant="primary" />
                    </div>
                ) : (
                    <div className="table-responsive">
                        <Table striped bordered hover>
                            <thead>
                                <tr>
                                    <th className="text-center" width="50">STT</th>
                                    <th>Tên tiểu luận</th>
                                    <th className="text-center">Từ ngày</th>
                                    <th className="text-center">Đến ngày</th>
                                    <th className="text-center">Trạng thái</th>
                                    <th className="text-center" width="120">Thao tác</th>
                                </tr>
                            </thead>
                        <tbody>
                            {dataList.length > 0 ? (
                                dataList.map((item, index) => (
                                    <tr key={item.id}>
                                        <td className="text-center">{index + 1}</td>
                                        <td>{item.tenTieuLuan}</td>
                                        <td className="text-center">
                                            {item.tuNgay ? moment(item.tuNgay).format('DD/MM/YYYY') : ''}
                                        </td>
                                        <td className="text-center">
                                            {item.denNgay ? moment(item.denNgay).format('DD/MM/YYYY') : ''}
                                        </td>
                                        <td className="text-center">{renderTrangThai(item.trangThai)}</td>
                                        <td className="text-center">
                                            {(() => {
                                                return (
                                                    <div className="d-flex justify-content-center text-nowrap">
                                                        <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Cập nhật trạng thái')}>
                                                            <Button variant="warning" size="sm" onClick={() => handleAction(item)} className="mr-1">
                                                                <i className="fas fa-tasks"></i>
                                                            </Button>
                                                        </OverlayTrigger>
                                                        <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Sửa dành cho HLĐT')}>
                                                            <Button variant="info" size="sm" onClick={() => { setDataEdit(item); setViewMode('edit'); }} className="mr-1">
                                                                <i className="fas fa-edit"></i>
                                                            </Button>
                                                        </OverlayTrigger>
                                                        <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Chấm tiểu luận')}>
                                                            <Button variant="primary" size="sm" onClick={() => { setDataEdit(item); setViewMode('chamTieuLuan'); }} className="mr-1">
                                                                <i className="fas fa-check-double"></i>
                                                            </Button>
                                                        </OverlayTrigger>
                                                        <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Xóa')}>
                                                            <Button variant="danger" size="sm" onClick={() => deleteAction(item.id, item.tenTieuLuan)}>
                                                                <i className="fas fa-trash"></i>
                                                            </Button>
                                                        </OverlayTrigger>
                                                    </div>
                                                );
                                            })()}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center text-muted">
                                        Chưa có dữ liệu Tiểu luận tổng quan
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>
                    </div>
                )}
            </Card.Body>
        </Card>

        <ConfirmModal
            isOpen={openConfirmDeleteModal}
            confirmType="delete"
            goBackAfterDelete={false}
            handleAccept={acceptDelete}
            handleCancel={cancelDelete}
            content={confirmDelMessage}
        />

        {showModal && (
            <Modal show={showModal} onHide={() => setShowModal(false)} backdrop="static" keyboard={false} size="md">
                <Modal.Header closeButton className="bg-primary text-white">
                    <Modal.Title>Cập nhật trạng thái Tiểu luận tổng quan</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Form.Group>
                            <Form.Label>Trạng thái</Form.Label>
                            <Form.Control 
                                as="select" 
                                value={trangThai}
                                onChange={(e) => setTrangThai(e.target.value)}
                            >
                                {objectTrangThaiTieuLuanTongQuan.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)} disabled={isSaving}>
                        <i className="fas fa-times mr-1"></i> Hủy
                    </Button>
                    <Button variant="primary" onClick={submitDuyet} disabled={isSaving}>
                        {isSaving ? 'Đang lưu...' : <><i className="fas fa-save mr-1"></i> Lưu lại</>}
                    </Button>
                </Modal.Footer>
            </Modal>
        )}
        
        {viewMode === 'edit' && dataEdit && (
            <TieuLuanTongQuanEdit hoSoId={hoSoId} dataEdit={dataEdit} setViewMode={setViewMode} refreshList={refetch} />
        )}
        {viewMode === 'chamTieuLuan' && dataEdit && (
            <ChamTieuLuanModal
                dataEdit={dataEdit}
                setViewMode={setViewMode}
                isView={false}
                isUserView={false}
            />
        )}
        {viewMode === 'add' && (
            <TieuLuanTongQuanAdd hoSoId={hoSoId} setViewMode={setViewMode} refreshList={refetch} />
        )}
        </>
    );
};

export default DuyetTieuLuanTongQuan;
